import * as DropdownMenu from '@radix-ui/react-dropdown-menu';
import { Check, Palette } from 'lucide-react';

import { THEMES, themeById } from '@/config/themes';
import { useTheme } from '@/hooks/use-theme';
import { cn } from '@/lib/utils';

/**
 * Pemilih tema di bilah atas.
 *
 * Menggantikan tombol terang/gelap yang lama. Dua tombol masih masuk akal
 * untuk dua pilihan; begitu ada empat, menggilirnya satu per satu berarti
 * pemakai harus melewati tema yang tidak ia inginkan untuk sampai ke yang ia
 * mau — dan tidak pernah tahu ada berapa banyak.
 *
 * Tiap baris membawa contoh warnanya sendiri, diambil dari `config/themes.ts`.
 * Tanpa itu "Neon Elegan" dan "Neon Modern" hanyalah dua nama yang menuntut
 * dicoba satu per satu untuk dibedakan.
 */
export function ThemePicker() {
  const { theme, setTheme } = useTheme();
  const active = themeById(theme);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          type="button"
          className="rounded-[var(--radius-sm)] p-2 text-[var(--color-muted-foreground)] transition-colors hover:bg-[var(--color-accent)] hover:text-[var(--color-foreground)]"
          aria-label={`Tema tampilan — sekarang ${active.label}`}
        >
          <Palette className="size-5" aria-hidden="true" />
        </button>
      </DropdownMenu.Trigger>

      <DropdownMenu.Portal>
        <DropdownMenu.Content
          align="end"
          sideOffset={8}
          className="z-50 min-w-60 rounded-[var(--radius-md)] border border-[var(--color-border)] bg-[var(--color-popover)] p-1 shadow-[var(--shadow-popover)]"
        >
          <p className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wider text-[var(--color-muted-foreground)]">
            Tema Tampilan
          </p>

          {THEMES.map((option) => {
            const isActive = option.id === theme;

            return (
              <DropdownMenu.Item
                key={option.id}
                onSelect={() => setTheme(option.id)}
                className={cn(
                  'flex cursor-pointer items-center gap-3 rounded-[var(--radius-sm)] px-3 py-2 outline-none',
                  'data-[highlighted]:bg-[var(--color-accent)]',
                  isActive && 'bg-[var(--color-primary-subtle)]',
                )}
              >
                {/* Contoh warna: latar, permukaan, aksen — tiga hal yang
                    paling menentukan kesan sebuah tema. */}
                <span
                  aria-hidden="true"
                  className="flex size-7 shrink-0 items-center overflow-hidden rounded-full border border-[var(--color-border)]"
                >
                  {option.swatch.map((color) => (
                    <span key={color} className="h-full flex-1" style={{ background: color }} />
                  ))}
                </span>

                <span className="min-w-0 flex-1">
                  <span
                    className={cn(
                      'block truncate text-sm',
                      isActive
                        ? 'font-medium text-[var(--color-primary-on-subtle)]'
                        : 'text-[var(--color-foreground)]',
                    )}
                  >
                    {option.label}
                  </span>
                  <span className="block truncate text-xs text-[var(--color-muted-foreground)]">
                    {option.description}
                  </span>
                </span>

                {isActive && (
                  <Check
                    className="size-4 shrink-0 text-[var(--color-primary-on-subtle)]"
                    aria-hidden="true"
                  />
                )}
              </DropdownMenu.Item>
            );
          })}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
